import React, { SetStateAction, useEffect, useState } from 'react';
import { MdPhotoCamera } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../store/authentication/authRedux';
import { IAllStates } from '../../../../../store/rootReducer';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { RatingStar } from '../../../../../shared/components/RatingStar';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User } from '../../../../../model/user/User';
import { multipartFormData } from '../../../../../utils';
import { PERSONAL_INFO_URL } from '../../../../../utils/endpoints';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import { IParams } from '../../../../../model/shared/IParams';
import { UserRoles } from '../../../../../enum/UserRoles';
import { InternalRoutes } from '../../../../../enum/InternalRoutes';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { Button, Col } from 'react-bootstrap';
import { ProfileInfoHeaderSkeleton } from '../../profile-view/components/ProfileSkeletons';

export type TProfileNavKey =
    | 'profile'
    | 'audienceData'
    | 'reviews'
    | 'cooperation_history'
    | 'cooperation_brand'
    | 'channel_collaborations';

export interface IProfileNavItem {
    key: TProfileNavKey;
    title: string;
    icon: React.ReactNode;
    count?: number;
    show: boolean;
}

interface IProfileInfoProps {
    isEdit: boolean;
    defaultState?: User;
    setUpdatePlatforms?: React.Dispatch<SetStateAction<boolean>>;
    navItems?: IProfileNavItem[];
    activeTab?: TProfileNavKey;
    onTabChange?: (key: TProfileNavKey) => void;
    onSubmit?: () => void;
    isLoading?: boolean;
}
interface ILogo {
    file: Blob[] | MediaSource[] | null;
}
const ProfileInfo = ({
    isEdit = false,
    defaultState,
    setUpdatePlatforms,
    navItems,
    activeTab,
    onTabChange,
    onSubmit,
    isLoading = false,
}: IProfileInfoProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();

    const [logo, setLogo] = useState<ILogo>({
        file: null,
    });
    const userType = useSelector((state: IAllStates) => state.auth.user.userType);
    const isPublicProfile = location.pathname.includes('/brand/');
    const isAgency = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles);

    const changeFileHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, files } = event.target;
        setLogo(prev => ({ ...prev, [name]: files }));
    };

    useEffect(() => {
        if (!!logo?.file?.length && setUpdatePlatforms) {
            multipartFormData(logo.file?.[0], `${PERSONAL_INFO_URL}/profile-photo/${id}`)
                .then(res => {
                    dispatch(actions.updateUser(res.data.data));
                    setUpdatePlatforms(prev => !prev);
                    setLogo({
                        file: null,
                    });
                    SuccessToast(t('general.toasts.successUpdatedPersonalInfo'));
                })
                .catch(error => ErrorToast(error));
        }
    }, [logo?.file]);

    const getProfileRoute = () => {
        if (!isEdit) {
            return `${InternalRoutes.INFLUENCER_PROFILE_EDIT}/${id}`;
        }
        return isAgency ? `${InternalRoutes.AGENCY_PROFILE}/${id}` : `${InternalRoutes.INFLUENCER_PROFILE}/${id}`;
    };

    const handleEditProfileClick = () => {
        navigate(getProfileRoute());
    };

    return (
        <CardLayout classBody="d-flex flex-column align-items-center">
            {isLoading ? (
                <ProfileInfoHeaderSkeleton />
            ) : (
                <div className="d-flex align-items-center justify-content-center pb-2 overflow-y-auto w-100">
                    <div className="position-relative pe-2">
                        <CustomImageHandler
                            thumbnailPath={defaultState?.profilePhoto?.thumbnail as string}
                            photoPath={defaultState?.profilePhoto?.thumbnail as string}
                            classes="profile-image size-md"
                            altTag="User Profile large"
                            name={
                                defaultState?.displayName
                                    ? defaultState.displayName
                                    : `${defaultState?.firstName ?? ''} ${defaultState?.lastName ?? ''}`.trim()
                            }
                        />
                        {isEdit && (
                            <div className="input-icon">
                                <div className="upload-image-icon">
                                    <MdPhotoCamera size={20} className="text-primary" />
                                    <input
                                        className="input-field px-1"
                                        type="file"
                                        name="file"
                                        onChange={changeFileHandler}
                                        placeholder="Username"
                                        accept="image/*"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div>
                        <h6>{defaultState?.displayName}</h6>
                        <p className="text-muted mb-2">{`${defaultState?.firstName ?? ''} ${defaultState?.lastName ?? ''}`.trim()}</p>
                        {defaultState?.totalRating ? <RatingStar rating={defaultState?.totalRating} /> : ''}
                    </div>
                </div>
            )}
            <div className={`profile-nav w-100  ${!isEdit && 'border-top border-bottom py-2'}`}>
                {!isEdit &&
                    navItems
                        ?.filter(item => item.show)
                        ?.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`profile-nav-item d-flex align-items-center justify-content-between w-100 ${
                                    item.key === activeTab ? 'profile-nav-item--active' : ''
                                }`}
                                onClick={() => onTabChange?.(item.key)}
                            >
                                <span className="d-flex align-items-center">
                                    <span className="icon-fixed me-2">{item.icon}</span>
                                    {item.title}
                                </span>
                            </button>
                        ))}
            </div>

            {isPublicProfile ? null : !isEdit ? (
                <Button className="w-100 mt-3" onClick={handleEditProfileClick} variant="primary">
                    {t('general.editProfile')}
                </Button>
            ) : (
                <div className="w-100 d-flex align-items-center justify-content-between">
                    <Button variant="outline-primary" className="w-100 mr-2" onClick={handleEditProfileClick}>
                        {t('general.button.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => {
                            if (onSubmit) {
                                onSubmit();
                            }
                        }}
                    >
                        {t('general.button.saveChanges')}
                    </Button>
                </div>
            )}
        </CardLayout>
    );
};

export default ProfileInfo;
